import {
  Show,
  SimpleShowLayout,
  TextField,
  Datagrid,
  ArrayField,
  ReferenceField,
  SingleFieldList,
  useShowController,
  ReferenceArrayField,
  FunctionField,
  Labeled,
  ChipField,
} from "react-admin";
import { GenderOptionsId } from "utils/choices";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import ChangeStatusConfirmModal from "./components/ChangeStatusConfirmModal";
import { accountRequests } from "api/accountApi";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";
import { getEmployeeProfiles } from "auth/security/securityUtils";
import { SectionTitle } from "features/SectionTitle";

const ShowEmployee = ({ ...props }) => {
  const { record } = useShowController(props);

  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  // Per-position roles only exist where position-based profiles are enabled (same gate as NodeItem).
  const positionRolesEnabled = getEmployeeProfiles()?.enabled === true;

  const inactiveStyle = !record?.isActive
    ? {
        filter: "grayscale(100%)",
        opacity: 0.8,
        backgroundColor: "#f5f5f5",
      }
    : {};

  return (
    <Show
      title="Детайли за служител"
      actions={<BackButtonToolbar />}
    >
      <Box sx={inactiveStyle}>
        <SimpleShowLayout>
          <SectionTitle
            title="Информация"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Grid
            container
            spacing={3}
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Име">
                <TextField source="userInfo.firstName" />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Презиме">
                <TextField source="userInfo.secondName" />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Фамилия">
                <TextField source="userInfo.lastName" />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="ЕГН">
                <TextField source="userInfo.personId" />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Пол">
                <FunctionField
                  render={record => GenderOptionsId.find(option => option.id === record.userInfo?.gender)?.name}
                />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Google Workspace имейл">
                <TextField source="corporateEmail" />
              </Labeled>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Телефони за контакт">
                <ArrayField source="phones">
                  <Datagrid
                    bulkActionButtons={false}
                    size="small"
                    rowClick={false}
                    sx={{
                      border: "none",
                      "& .RaDatagrid-headerCell": {
                        display: "none",
                        border: "none",
                      },
                      "& .RaDatagrid-rowCell": {
                        border: "none",
                        padding: 0,
                      },
                    }}
                  >
                    <TextField
                      label=" "
                      source="phoneNumber"
                    />
                  </Datagrid>
                </ArrayField>
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Научна степен">
                <ReferenceArrayField
                  source="scientificDegrees"
                  reference={employeeRequests.getScientificDegreesSelect()}
                >
                  <SingleFieldList
                    linkType={false}
                    empty="-"
                  >
                    <ChipField source="name" />
                  </SingleFieldList>
                </ReferenceArrayField>
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Labeled label="Статус">
                  <FunctionField render={rec => (rec.isActive ? "Активен" : "Неактивен")} />
                </Labeled>

                <SecuredComponent permission={permissions.canUpdateEmployees}>
                  <ChangeStatusConfirmModal
                    id={record?.id}
                    isActive={record?.isActive}
                  />
                </SecuredComponent>
              </Grid>
            </Grid>
          </Grid>
        </SimpleShowLayout>
        <SimpleShowLayout>
          <SectionTitle
            title="Данни за ЛК"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Grid
            container
            spacing={3}
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            {record?.idCardDetail && (
              <>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <Labeled label="Номер на лична карта">
                    <TextField
                      source="idCardDetail.number"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <Labeled label="Дата на издаване на лична карта">
                    <TextField
                      source="idCardDetail.issuedOn"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <Labeled label="Издател на лична карта">
                    <TextField
                      source="idCardDetail.issuedBy"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <Labeled label="Адрес по лична карта">
                    <TextField
                      source="idCardDetail.address"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>
              </>
            )}
          </Grid>
        </SimpleShowLayout>
        <SimpleShowLayout>
          <SectionTitle
            title="Длъжности"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />

          <Grid
            container
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            <Grid
              item
              xs={12}
            >
              <ArrayField
                source="nodeEmployees"
                label=""
              >
                <Datagrid
                  bulkActionButtons={false}
                  rowClick={false}
                  sx={settings.style.secondaryDatagrid}
                >
                  <ReferenceField
                    source="structuralUnitId"
                    label="Структурна единица"
                    reference={administrationRequests.getStructuralUnitsSelect()}
                  >
                    <TextField source="name" />
                  </ReferenceField>

                  <ArrayField
                    source="positions"
                    label="Детайли за позицията"
                  >
                    <Datagrid
                      bulkActionButtons={false}
                      rowClick={false}
                      sx={settings.style.detailsDatagrid}
                    >
                      <ReferenceField
                        source="positionId"
                        label="Длъжност"
                        reference={employeeRequests.getPositionsSelect()}
                      >
                        <TextField source="name" />
                      </ReferenceField>

                      <FunctionField
                        label="Ръководител"
                        render={record => (record.isManager ? "Да" : "Не")}
                      />

                      <FunctionField
                        label="Хоноруван"
                        render={record => (record.isPartTime ? "Да" : "Не")}
                      />

                      {positionRolesEnabled && (
                        <ReferenceArrayField
                          source="roleIds"
                          label="Роли за позицията"
                          reference={accountRequests.getRolesSelect()}
                        >
                          <SingleFieldList
                            linkType={false}
                            empty="-"
                          >
                            <ChipField source="name" />
                          </SingleFieldList>
                        </ReferenceArrayField>
                      )}
                    </Datagrid>
                  </ArrayField>
                </Datagrid>
              </ArrayField>
            </Grid>
          </Grid>
        </SimpleShowLayout>

        <SimpleShowLayout>
          <SectionTitle
            title="Роли"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Grid
            container
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            <Grid
              item
              xs={12}
              md={12}
            >
              <Labeled label="Роля">
                <ReferenceArrayField
                  source="roleIds"
                  reference={accountRequests.getRolesSelect()}
                >
                  <SingleFieldList
                    label="Роля"
                    source="name"
                    linkType={false}
                  />
                </ReferenceArrayField>
              </Labeled>
            </Grid>
          </Grid>
        </SimpleShowLayout>

        <SimpleShowLayout>
          <SectionTitle
            title="Права"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Grid
            container
            sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
          >
            <Grid
              item
              xs={12}
              md={12}
            >
              <Labeled label="Права">
                <ReferenceArrayField
                  source="permissionIds"
                  reference={accountRequests.getPermissionsSelect()}
                >
                  <SingleFieldList
                    label="Права"
                    source="name"
                    linkType={false}
                  />
                </ReferenceArrayField>
              </Labeled>
            </Grid>
          </Grid>
        </SimpleShowLayout>
      </Box>
    </Show>
  );
};

export default ShowEmployee;
